import React, { useState, useEffect } from 'react';
import { Shield, MapPin, Save, Loader2, ToggleLeft, ToggleRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import AddressAutocomplete from './AddressAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL;

const CAPABILITY_OPTIONS = [
  { key: 'free_hanging_swarms', label: 'Free-hanging swarms (tree, branch, fence)' },
  { key: 'ground_level', label: 'Ground-level swarms' },
  { key: 'elevated', label: 'Elevated swarms (above head height)' },
  { key: 'cut_outs', label: 'Cut-outs (walls, floors, cavities)' },
  { key: 'roof_ceiling', label: 'Roof/ceiling removals' },
  { key: 'chimney', label: 'Chimney removals' },
  { key: 'established_colony', label: 'Established colony removals' },
];

const EQUIPMENT_OPTIONS = [
  { key: 'own_ladder', label: 'Own ladder' },
  { key: 'bee_vacuum', label: 'Bee vacuum' },
  { key: 'thermal_camera', label: 'Thermal camera' },
  { key: 'repair_capability', label: 'Repair capability (patch/seal after removal)' },
];

const RADIUS_OPTIONS = [5, 10, 15, 25, 50, 100];

export default function SwarmCollectionSettings() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/api/swarm-collections/my-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProfile(res.data))
      .catch(() => toast.error('Failed to load collection profile'));
  }, []);

  const update = (key, val) => setProfile(p => ({ ...p, [key]: val }));
  const updateCap = (key, val) => setProfile(p => ({ ...p, capabilities: { ...p.capabilities, [key]: val } }));
  const updateEquip = (key, val) => setProfile(p => ({ ...p, equipment: { ...p.equipment, [key]: val } }));

  const detectLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const addr = res.data?.display_name || '';
          setProfile(p => ({ ...p, base_latitude: latitude, base_longitude: longitude, base_address: addr }));
          toast.success('Location detected');
        } catch {
          setProfile(p => ({ ...p, base_latitude: latitude, base_longitude: longitude }));
          toast.success('Coordinates captured');
        }
        setLocating(false);
      },
      () => { setLocating(false); toast.error('Could not detect location'); },
      { enableHighAccuracy: true }
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/swarm-collections/my-profile`, profile, { headers: { Authorization: `Bearer ${token}` } });
      // Re-fetch to get server-geocoded coordinates
      const res = await axios.get(`${API}/api/swarm-collections/my-profile`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      if (res.data.base_latitude && !profile.base_latitude) {
        toast.success('Profile saved — address geocoded successfully');
      } else {
        toast.success('Collection profile saved');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="p-8 text-center text-stone-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
        <Shield className="h-6 w-6 text-amber-600" />
        Swarm Rescue Settings
      </h2>

      {/* Availability Toggle */}
      <Card className={profile.available_for_collections ? 'border-green-300 bg-green-50' : 'border-stone-200'}>
        <CardContent className="p-4">
          <button onClick={() => update('available_for_collections', !profile.available_for_collections)} className="flex items-center gap-3 w-full" data-testid="collection-availability-toggle">
            {profile.available_for_collections ? <ToggleRight className="h-8 w-8 text-green-600" /> : <ToggleLeft className="h-8 w-8 text-stone-400" />}
            <div>
              <span className="font-bold text-stone-800">Available for Swarm Rescues</span>
              <p className="text-sm text-stone-500">{profile.available_for_collections ? 'You will receive swarm alerts' : 'You will not receive swarm alerts'}</p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Base Location */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">Your Base Location</h3>
          <p className="text-sm text-stone-500">Swarms are matched based on distance from your base</p>
          <div className="flex gap-2">
            <AddressAutocomplete
              value={profile.base_address || ''}
              onChange={({ address, lat, lon }) => {
                setProfile(p => ({ ...p, base_address: address, base_latitude: lat, base_longitude: lon }));
              }}
              placeholder="Start typing your address..."
              data-testid="base-address-input"
            />
            <Button type="button" onClick={detectLocation} disabled={locating} variant="outline" title="Use GPS location">
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            </Button>
          </div>
          {profile.base_latitude ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-green-800">Location confirmed</span>
                <span className="text-xs text-green-600 ml-2">({profile.base_latitude?.toFixed(4)}, {profile.base_longitude?.toFixed(4)})</span>
              </div>
            </div>
          ) : profile.base_address ? (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">Address entered — click <strong>Save</strong> to geocode, or use the GPS button for pinpoint accuracy</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
              <MapPin className="h-4 w-4 text-stone-400 flex-shrink-0" />
              <span className="text-sm text-stone-500">Enter your address or use the GPS button to set your base location</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Collection Radius */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">Collection Radius</h3>
          <div className="flex flex-wrap gap-2">
            {RADIUS_OPTIONS.map(r => (
              <button key={r} onClick={() => update('collection_radius_km', r)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${profile.collection_radius_km === r ? 'bg-amber-500 text-white border-amber-500' : 'border-stone-300 hover:border-amber-400'}`} data-testid={`radius-${r}km`}>
                {r} km
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Capabilities */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">What are you comfortable collecting?</h3>
          <div className="space-y-2">
            {CAPABILITY_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                <input type="checkbox" checked={profile.capabilities?.[opt.key] || false} onChange={e => updateCap(opt.key, e.target.checked)} className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500" />
                <span className="text-sm text-stone-700">{opt.label}</span>
              </label>
            ))}
          </div>
          {profile.capabilities?.elevated && (
            <div className="ml-7">
              <Label className="text-sm text-stone-600">Maximum comfortable height (metres)</Label>
              <Input type="number" value={profile.max_height_m || ''} onChange={e => update('max_height_m', parseFloat(e.target.value) || 0)} placeholder="e.g. 5" className="w-32 mt-1" data-testid="max-height-input" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equipment */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">Equipment</h3>
          <div className="space-y-2">
            {EQUIPMENT_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                <input type="checkbox" checked={profile.equipment?.[opt.key] || false} onChange={e => updateEquip(opt.key, e.target.checked)} className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500" />
                <span className="text-sm text-stone-700">{opt.label}</span>
              </label>
            ))}
            {profile.equipment?.own_ladder && (
              <div className="ml-7">
                <Label className="text-sm text-stone-600">Ladder max height (metres)</Label>
                <Input type="number" value={profile.equipment?.ladder_max_height_m || ''} onChange={e => updateEquip('ladder_max_height_m', parseFloat(e.target.value) || 0)} placeholder="e.g. 6" className="w-32 mt-1" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preferred Times */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">Preferred Collection Times</h3>
          <div className="flex flex-wrap gap-2">
            {['Anytime', 'Mornings', 'Afternoons', 'Evenings', 'Weekdays only', 'Weekends only'].map(t => (
              <button key={t} onClick={() => update('preferred_times', t.toLowerCase())} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${profile.preferred_times === t.toLowerCase() ? 'bg-amber-500 text-white border-amber-500' : 'border-stone-300 hover:border-amber-400'}`}>
                {t}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Available Days */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-stone-800">Available Days</h3>
          <p className="text-sm text-stone-500">Select the days you're available for collections</p>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
              { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' }, { key: 'sun', label: 'Sun' },
            ].map(d => {
              const days = profile.availability_days || ['mon','tue','wed','thu','fri','sat','sun'];
              const active = days.includes(d.key);
              return (
                <button key={d.key} onClick={() => {
                  const current = profile.availability_days || ['mon','tue','wed','thu','fri','sat','sun'];
                  update('availability_days', active ? current.filter(x => x !== d.key) : [...current, d.key]);
                }} className={`w-12 h-10 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-amber-500 text-white border-amber-500' : 'border-stone-300 text-stone-500 hover:border-amber-400'}`}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Spacer for fixed save button */}
      <div className="h-20" />

      {/* Fixed Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-stone-200 py-3 px-4 z-50 md:pl-[200px]">
        <div className="max-w-4xl mx-auto">
          <Button onClick={handleSave} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-lg" data-testid="save-collection-profile-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Collection Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
}
